import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getPortfolioList, PortfolioInfo } from "api/student/v2/portfolio.api";
import { getImageUrl } from "helpers/Image.helper";

const Portfolio = (props: any) => {
    const navigate = useNavigate();
    const [portfolioList, setPortfolioList] = useState<PortfolioInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortBy, setSortBy] = useState("recent"); // "recent" | "rating" | "views"

    // 등급별 색상 및 스타일
    const getGradeStyle = (gradeScore: number) => {
        let grade = "F";
        if (gradeScore >= 90) grade = "S";
        else if (gradeScore >= 80) grade = "A";
        else if (gradeScore >= 70) grade = "B";
        else if (gradeScore >= 60) grade = "C";
        else if (gradeScore >= 50) grade = "D";
        else if (gradeScore >= 40) grade = "E";

        const styles = {
            S: "bg-yellow-500 text-white font-bold border-2 border-yellow-300",
            A: "bg-red-500 text-white font-bold border-2 border-red-300",
            B: "bg-orange-500 text-white font-bold border-2 border-orange-300",
            C: "bg-blue-500 text-white font-bold border-2 border-blue-300",
            D: "bg-green-500 text-white font-bold border-2 border-green-300",
            E: "bg-purple-500 text-white font-bold border-2 border-purple-300",
            F: "bg-gray-500 text-white font-bold border-2 border-gray-300"
        };
        return styles[grade as keyof typeof styles] || styles.F;
    };

    // 포트폴리오 목록 조회
    const fetchPortfolioList = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getPortfolioList(1, 50); // 페이지 1, 50개씩
            setPortfolioList(data.list);
        } catch (error) {
            console.error('포트폴리오 목록 조회 실패:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPortfolioList();
    }, [fetchPortfolioList]);

    const handleSort = (sortType: string) => {
        setSortBy(sortType);
        let sortedList = [...portfolioList];
        
        switch (sortType) {
            case "rating":
                sortedList.sort((a, b) => b.gradeScore - a.gradeScore);
                break;
            case "views":
                sortedList.sort((a, b) => b.viewCount - a.viewCount);
                break;
            case "recent":
            default:
                sortedList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                break;
        }
        
        setPortfolioList(sortedList);
    };

    const handlePortfolioClick = (portfolioId: number) => {
        navigate(`/student/profile/portfolio/${portfolioId}`);
    };

    const getGradeFromScore = (gradeScore: number) => {
        if (gradeScore >= 90) return "S";
        if (gradeScore >= 80) return "A";
        if (gradeScore >= 70) return "B";
        if (gradeScore >= 60) return "C";
        if (gradeScore >= 50) return "D";
        if (gradeScore >= 40) return "E";
        return "F";
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).replace(/\./g, '').replace(/\s/g, '.');
    };

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-6 lg:pt-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "profilePortfolio.txtTitle")}
                </h2>
                <p className="text-gray-600 mb-8">
                    {getLanguageValue(PAGE_TYPE.STUDENT, "profilePortfolio.txtDesc")}
                </p>

                <div className="max-w-7xl mx-auto space-y-6">
                    {/* 필터 및 정렬 섹션 */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                            {/* 정렬 옵션 */}
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-600">정렬:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => handleSort(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#CF3688]/20 focus:border-[#CF3688] text-sm"
                                >
                                    <option value="recent">최신순</option>
                                    <option value="rating">평점순</option>
                                    <option value="views">조회수순</option>
                                </select>
                            </div>
                            
                            <button 
                                onClick={() => navigate('/student/profile/portfolio/create')}
                                className="bg-[#CF3688] text-white px-6 py-3 rounded-lg hover:bg-[#B52E74] transition-colors font-medium"
                            >
                                + 새 포트폴리오
                            </button>
                        </div>
                    </div>

                    {/* 포트폴리오 목록 */}
                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#CF3688]"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            {portfolioList.map((portfolio) => (
                                <div
                                    key={portfolio.id}
                                    className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                    onClick={() => handlePortfolioClick(portfolio.id)}
                                >
                                    {/* 썸네일 영역 */}
                                    <div className="relative">
                                        <img
                                            src={getImageUrl(portfolio.thumbnailImage)}
                                            alt={portfolio.title}
                                            className="w-full h-48 sm:h-40 object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <div className="bg-white bg-opacity-90 rounded-full p-2 sm:p-3">
                                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-[#CF3688]" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="absolute top-3 left-3 z-10">
                                            <div className={`px-2 py-1 rounded-full text-xs font-bold shadow-lg ${getGradeStyle(portfolio.gradeScore)}`}>
                                                {getGradeFromScore(portfolio.gradeScore)}
                                            </div>
                                        </div>
                                        <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                                            {formatDate(portfolio.createdAt)}
                                        </div>
                                    </div>

                                    {/* 내용 영역 */}
                                    <div className="p-4">
                                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-3 line-clamp-2">
                                            {portfolio.title}
                                        </h3>

                                        {/* 핵심 지표들 */}
                                        <div className="space-y-3">
                                            {/* 공개 설정 */}
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-xs text-gray-500">공개:</span>
                                                <div className="flex flex-wrap gap-1">
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                        portfolio.isPublicToAgency 
                                                            ? 'bg-green-100 text-green-800' 
                                                            : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        기획사
                                                    </span>
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                        portfolio.isPublicToKoach 
                                                            ? 'bg-blue-100 text-blue-800' 
                                                            : 'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        코치
                                                    </span>
                                                </div>
                                            </div>

                                            {/* 등급 점수 */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-2">
                                                    <svg className="w-4 h-4 text-[#CF3688]" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                                    </svg>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {portfolio.gradeScore}
                                                    </span>
                                                </div>
                                                <span className="text-xs text-gray-500">
                                                    등급 점수
                                                </span>
                                            </div>

                                            {/* 조회수 */}
                                            <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100">
                                                <span className="flex items-center">
                                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                    {portfolio.viewCount.toLocaleString()}회 조회
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* 빈 상태 */}
                    {!isLoading && portfolioList.length === 0 && (
                        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                아직 포트폴리오가 없습니다
                            </h3>
                            <p className="text-gray-500 mb-6">
                                첫 번째 포트폴리오를 업로드해보세요!
                            </p>
                            <button 
                                onClick={() => navigate('/student/profile/portfolio/create')}
                                className="bg-[#CF3688] text-white px-6 py-3 rounded-lg hover:bg-[#B52E74] transition-colors font-medium"
                            >
                                포트폴리오 업로드하기
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default Portfolio;